import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Progress } from '../../ui/progress';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Alert, AlertDescription } from '../../ui/alert';
import {
  BarChart3,
  TrendingUp,
  TrendingDown,
  Users,
  FileText,
  CheckCircle,
  AlertCircle,
  Clock,
  Target,
  Award,
  RefreshCw,
  Info
} from 'lucide-react';
import { useToast } from '../../ui/use-toast';
import axios from 'axios';

const FitCulturalDashboard = () => {
  const { toast } = useToast();
  
  const [loading, setLoading] = useState(true);
  const [resumoGlobal, setResumoGlobal] = useState({
    ifc_medio_geral: 0,
    taxa_aprovacao_geral: 0,
    total_avaliacoes_periodo: 0
  });
  const [avaliacoesDisponiveis, setAvaliacoesDisponiveis] = useState([]);
  const [aplicacoes, setAplicacoes] = useState({
    pendentes: [],
    concluidas: []
  });

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      // Buscar dados em paralelo
      const [concluidasRes, pendentesRes, avaliacoesRes] = await Promise.all([
        axios.get(`${backendUrl}/api/fit-cultural/concluidas`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }),
        axios.get(`${backendUrl}/api/fit-cultural/pendentes`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }),
        axios.get(`${backendUrl}/api/fit-cultural/avaliacoes`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        })
      ]);

      const concluidas = concluidasRes.data;
      const pendentes = pendentesRes.data;
      const avaliacoes = avaliacoesRes.data;

      // Calcular resumo global
      if (concluidas.length > 0) {
        const scores = concluidas.map(a => (a.ifc_calculado || 0) * 100);
        const scoreMedia = scores.reduce((a, b) => a + b, 0) / scores.length;
        
        setResumoGlobal({
          ifc_medio_geral: Math.round(scoreMedia),
          taxa_aprovacao_geral: Math.round((scores.filter(s => s > 60).length / scores.length) * 100),
          total_avaliacoes_periodo: concluidas.length
        });
      }

      setAvaliacoesDisponiveis(avaliacoes);
      setAplicacoes({
        pendentes,
        concluidas
      });
      
    } catch (error) {
      console.error('Erro ao carregar dashboard:', error);
      toast({
        title: "Erro",
        description: "Erro ao carregar dados do dashboard",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Calcular estatísticas de aplicações por avaliação
  const getEstatisticasAvaliacoes = () => {
    const stats = {};
    
    avaliacoesDisponiveis.forEach(av => {
      const aplicadasPendentes = aplicacoes.pendentes.filter(p => p.avaliacao_id === av.id).length;
      const aplicadasConcluidas = aplicacoes.concluidas.filter(c => c.avaliacao_id === av.id).length;
      
      stats[av.nome] = {
        total: aplicadasPendentes + aplicadasConcluidas,
        pendentes: aplicadasPendentes,
        concluidas: aplicadasConcluidas,
        cor: getCorAleatoria(av.id)
      };
    });
    
    return stats;
  };

  const getCorAleatoria = (id) => {
    const cores = [
      'bg-blue-500',
      'bg-purple-500',
      'bg-pink-500',
      'bg-green-500',
      'bg-yellow-500',
      'bg-red-500',
      'bg-indigo-500',
      'bg-teal-500'
    ];
    const index = id ? id.charCodeAt(0) % cores.length : 0;
    return cores[index];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600" />
          <p className="text-gray-600">Carregando dashboard...</p>
        </div>
      </div>
    );
  }

  const estatisticasAvaliacoes = getEstatisticasAvaliacoes();

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Dashboard Fit Cultural</h1>
          <p className="text-gray-600 mt-1">Visão geral das avaliações e resultados</p>
        </div>
        <Button onClick={loadDashboardData} variant="outline" className="flex items-center gap-2">
          <RefreshCw className="w-4 h-4" />
          Atualizar
        </Button>
      </div>

      {/* Cards Principais */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* IFC Médio Geral */}
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium text-blue-800">IFC Médio Geral</CardTitle>
              <Target className="w-5 h-5 text-blue-600" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-4xl font-bold text-blue-900 mb-2">
              {resumoGlobal.ifc_medio_geral}%
            </div>
            <Progress value={resumoGlobal.ifc_medio_geral} className="h-2 mb-2" />
            <div className="flex items-center gap-1 text-xs text-blue-700">
              {resumoGlobal.ifc_medio_geral > 60 ? (
                <>
                  <TrendingUp className="w-3 h-3" />
                  <span>Fit Alto</span>
                </>
              ) : resumoGlobal.ifc_medio_geral > 30 ? (
                <>
                  <TrendingDown className="w-3 h-3" />
                  <span>Fit Médio</span>
                </>
              ) : (
                <>
                  <AlertCircle className="w-3 h-3" />
                  <span>Fit Baixo</span>
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Taxa de Aprovação */}
        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium text-green-800">Taxa de Aprovação</CardTitle>
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-4xl font-bold text-green-900 mb-2">
              {resumoGlobal.taxa_aprovacao_geral}%
            </div>
            <Progress value={resumoGlobal.taxa_aprovacao_geral} className="h-2 mb-2" />
            <p className="text-xs text-green-700">
              Avaliações com IFC &gt; 60%
            </p>
          </CardContent>
        </Card>

        {/* Total de Avaliações */}
        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium text-purple-800">Total de Avaliações</CardTitle>
              <Users className="w-5 h-5 text-purple-600" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-4xl font-bold text-purple-900 mb-2">
              {resumoGlobal.total_avaliacoes_periodo}
            </div>
            <p className="text-xs text-purple-700 mt-2">
              Avaliações finalizadas
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Gráficos e Estatísticas */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Avaliações Criadas vs Aplicadas */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              Avaliações Criadas vs Aplicadas
            </CardTitle>
            <CardDescription>
              Total de {avaliacoesDisponiveis.length} avaliações configuradas no sistema
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {avaliacoesDisponiveis.map((avaliacao) => {
                const stats = estatisticasAvaliacoes[avaliacao.nome] || { total: 0, pendentes: 0, concluidas: 0 };
                const maxAplicacoes = Math.max(...Object.values(estatisticasAvaliacoes).map(s => s.total), 1);
                const percentage = (stats.total / maxAplicacoes) * 100;
                
                return (
                  <div key={avaliacao.id} className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium text-gray-700 truncate pr-2">
                        {avaliacao.nome}
                      </span>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs">
                          {stats.total} aplicada{stats.total !== 1 ? 's' : ''}
                        </Badge>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-gray-200 rounded-full h-3 overflow-hidden">
                        <div 
                          className={`h-full ${stats.cor} transition-all duration-500`}
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                    <div className="flex gap-3 text-xs text-gray-600">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3 text-yellow-600" />
                        {stats.pendentes} pendente{stats.pendentes !== 1 ? 's' : ''}
                      </span>
                      <span className="flex items-center gap-1">
                        <CheckCircle className="w-3 h-3 text-green-600" />
                        {stats.concluidas} concluída{stats.concluidas !== 1 ? 's' : ''}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Status das Aplicações */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-purple-600" />
              Status das Aplicações
            </CardTitle>
            <CardDescription>
              Distribuição entre pendentes e concluídas
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {/* Gráfico de barras simples */}
              <div className="space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">Pendentes</span>
                    <Badge className="bg-yellow-100 text-yellow-800">
                      {aplicacoes.pendentes.length}
                    </Badge>
                  </div>
                  <div className="bg-gray-200 rounded-full h-8 overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-yellow-400 to-yellow-500 flex items-center justify-end pr-3"
                      style={{ 
                        width: `${(aplicacoes.pendentes.length / (aplicacoes.pendentes.length + aplicacoes.concluidas.length || 1)) * 100}%`,
                        minWidth: '40px'
                      }}
                    >
                      <span className="text-xs font-bold text-white">
                        {Math.round((aplicacoes.pendentes.length / (aplicacoes.pendentes.length + aplicacoes.concluidas.length || 1)) * 100)}%
                      </span>
                    </div>
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">Concluídas</span>
                    <Badge className="bg-green-100 text-green-800">
                      {aplicacoes.concluidas.length}
                    </Badge>
                  </div>
                  <div className="bg-gray-200 rounded-full h-8 overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-green-400 to-green-500 flex items-center justify-end pr-3"
                      style={{ 
                        width: `${(aplicacoes.concluidas.length / (aplicacoes.pendentes.length + aplicacoes.concluidas.length || 1)) * 100}%`,
                        minWidth: '40px'
                      }}
                    >
                      <span className="text-xs font-bold text-white">
                        {Math.round((aplicacoes.concluidas.length / (aplicacoes.pendentes.length + aplicacoes.concluidas.length || 1)) * 100)}%
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Resumo */}
              <div className="pt-4 border-t border-gray-200">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium text-gray-700">Total de Aplicações</span>
                  <Badge variant="outline" className="text-base font-bold">
                    {aplicacoes.pendentes.length + aplicacoes.concluidas.length}
                  </Badge>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Guia de Interpretação */}
      <Card className="border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-white">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-blue-900">
            <Info className="w-5 h-5" />
            Guia de Interpretação dos Resultados
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Fit Alto */}
            <Alert className="bg-gradient-to-br from-green-50 to-green-100 border-green-300">
              <Award className="h-5 w-5 text-green-700" />
              <AlertDescription className="mt-2">
                <div className="font-bold text-green-900 text-lg mb-2">Fit Alto (61-100%)</div>
                <p className="text-sm text-green-800 mb-2">
                  <strong>✓ Aprovado</strong>
                </p>
                <p className="text-xs text-green-700">
                  Forte alinhamento com os valores da empresa. Candidato/funcionário demonstra 
                  comportamentos consistentes com a cultura organizacional.
                </p>
              </AlertDescription>
            </Alert>

            {/* Fit Médio */}
            <Alert className="bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-300">
              <AlertCircle className="h-5 w-5 text-yellow-700" />
              <AlertDescription className="mt-2">
                <div className="font-bold text-yellow-900 text-lg mb-2">Fit Médio (31-60%)</div>
                <p className="text-sm text-yellow-800 mb-2">
                  <strong>⚠ Requer Atenção</strong>
                </p>
                <p className="text-xs text-yellow-700">
                  Alinhamento moderado. Pode se adaptar com desenvolvimento e acompanhamento. 
                  Avaliar pontos específicos de desenvolvimento.
                </p>
              </AlertDescription>
            </Alert>

            {/* Fit Baixo */}
            <Alert className="bg-gradient-to-br from-red-50 to-red-100 border-red-300">
              <TrendingDown className="h-5 w-5 text-red-700" />
              <AlertDescription className="mt-2">
                <div className="font-bold text-red-900 text-lg mb-2">Fit Baixo (0-30%)</div>
                <p className="text-sm text-red-800 mb-2">
                  <strong>✗ Reprovado</strong>
                </p>
                <p className="text-xs text-red-700">
                  Baixo alinhamento com os valores da empresa. Pode gerar conflitos culturais. 
                  Recomenda-se não prosseguir com o processo.
                </p>
              </AlertDescription>
            </Alert>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default FitCulturalDashboard;
